import { MySkillMapQuery } from 'core';

type MapNode = NonNullable<MySkillMapQuery['mySkillMap']>;

export interface ISkillDemand {
  liveVacancies: number;
  /** 0 — не встречается на доске, 3 — в топе. */
  band: number;
}

export interface IMySkill {
  id: string;
  name: string;
  slug: string;
  category: string;
  /** «Что это и зачем» — текст справочника. Пустая строка = не заполнено. */
  description: string;
  /** Своя картинка из ERP; `null` — рисуем знак по слагу или букву. */
  iconUrl: string | null;
  level: number;
  /** 'course' — подтверждено школой, 'self' — заявлено самим. */
  source: string;
  isHidden: boolean;
  grantedCourseName: string | null;
  /** ТЗ 0040: занятие, на котором навык открылся. */
  grantedLessonNumber: number | null;
  demand: ISkillDemand | null;
  /** Наши курсы, где этот навык дают, — «где углубить». */
  providers: ISkillProvider[];
}

export interface ISkillProvider {
  courseId: string;
  courseName: string;
  /** Страница курса на сайте школы: в ЛМС витрины курсов нет. */
  courseUrl: string;
  level: number;
  completed: boolean;
}

export interface IMissingSkill {
  id: string;
  name: string;
  slug: string;
  category: string;
  description: string;
  iconUrl: string | null;
  demand: ISkillDemand | null;
  providers: ISkillProvider[];
  /**
   * ТЗ 0040 доводка: навык откроется на курсе, где студент УЖЕ учится.
   * `null` — ни один текущий курс его не даёт, и «где взять» остаётся
   * единственным честным ответом.
   */
  upcoming: { lessonNumber: number; lessonsLeft: number } | null;
}

export interface INextCourse {
  courseId: string;
  courseName: string;
  courseUrl: string;
  /** Какие навыки трека закроет этот курс — из них строится «зачем». */
  adds: string[];
  covered: number;
  totalMissing: number;
}

export interface ISkillMap {
  trackCode: string | null;
  trackTitle: string | null;
  /** Дата снимка востребованности; `null` — снимок ещё не считали. */
  demandComputedAt: string | null;
  mine: IMySkill[];
  missing: IMissingSkill[];
  nextCourses: INextCourse[];
}

/** Один и тот же курс-провайдер и у своих навыков, и у недостающих. */
const providerOf = (provider: {
  courseId: string;
  courseName: string;
  courseUrl: string;
  level: number;
  completed: boolean;
}): ISkillProvider => ({
  courseId: provider.courseId,
  courseName: provider.courseName,
  courseUrl: provider.courseUrl,
  level: provider.level,
  completed: provider.completed,
});

const demandOf = (
  node: { liveVacancies: number; band: number } | null | undefined,
): ISkillDemand | null => (node ? { liveVacancies: node.liveVacancies, band: node.band } : null);

/** ТЗ 0039 шаг 5. Разворачивает вложенный `skill` наверх — экрану так удобнее. */
export const transformSkillMap = (node: MapNode | null | undefined): ISkillMap | null => {
  if (!node) return null;

  return {
    trackCode: node.trackCode ?? null,
    trackTitle: node.trackTitle ?? null,
    demandComputedAt: node.demandComputedAt ?? null,
    nextCourses: node.nextCourses.map((row) => ({
      courseId: row.courseId,
      courseName: row.courseName,
      courseUrl: row.courseUrl,
      adds: [...row.adds],
      covered: row.covered,
      totalMissing: row.totalMissing,
    })),
    mine: node.mine.map((row) => ({
      id: row.skill.id,
      name: row.skill.name,
      slug: row.skill.slug,
      category: row.skill.category,
      description: row.skill.description ?? '',
      iconUrl: row.skill.iconUrl ?? null,
      level: row.level,
      source: row.source,
      isHidden: row.isHidden,
      grantedCourseName: row.grantedCourseName ?? null,
      grantedLessonNumber: row.grantedLessonNumber ?? null,
      demand: demandOf(row.demand),
      providers: row.providers.map(providerOf),
    })),
    missing: node.missing.map((row) => ({
      id: row.skill.id,
      name: row.skill.name,
      slug: row.skill.slug,
      category: row.skill.category,
      description: row.skill.description ?? '',
      iconUrl: row.skill.iconUrl ?? null,
      demand: demandOf(row.demand),
      providers: row.providers.map(providerOf),
      upcoming: row.upcoming
        ? {
            lessonNumber: row.upcoming.lessonNumber,
            lessonsLeft: row.upcoming.lessonsLeft,
          }
        : null,
    })),
  };
};
